import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import "react-datepicker/dist/react-datepicker.css";
import { BACKEND_URL_OUT, makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { countries } from "./CountryName";
import { User, Calendar, MapPin, Globe, Languages, Image as ImageIcon, Loader2, ChevronRight, Edit3 } from "lucide-react";


// ✅ MOVED OUTSIDE (VERY IMPORTANT)
const FormField = React.memo(({ label, icon: Icon, children, error }) => (
  <div className="flex flex-col gap-1.5 w-full">
    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
      {Icon && <Icon size={16} className="text-[#132b45]" />}
      {label}
    </label>
    {children}
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
));

const InfoCard = ({ label, value }) => {

  const isStatus = label === "Status";

  const getStatusStyle = (status) => {
    switch (status) {
      case "approve":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "under_review":
        return "bg-blue-100 text-blue-700";
      case "reject":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div className="p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition">
      <p className="text-xs text-gray-400 uppercase tracking-wider">
        {label}
      </p>

      {isStatus ? (
        <span
          className={`inline-block mt-2 px-3 py-1 text-xs font-semibold rounded-full capitalize ${getStatusStyle(value)}`}
        >
          {value.replace("_", " ")}
        </span>
      ) : (
        <p className="font-semibold text-sm text-[#132b45] mt-1 capitalize">
          {value || "N/A"}
        </p>
      )}
    </div>
  );
};


const ApplicantForm = ({ fetchProfileUser }) => {

  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    dateOfBirth: null,
    placeOfBirth: "",
    citizenship: "",
    otherCitizen: "",
    otherCountry: "",
    motherTongue: "",
    religion: "",
    gender: "",
    height: "",
    casteImageUrl: "",
    isSpouseApplication: false,
  });

  const inputClass =
    "w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#132b45]/20 focus:border-[#132b45] outline-none transition-all bg-gray-50/50 hover:bg-white";

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await makeRequestAuth("get", "/user/getBasicProfile");

      if (!res?.data) return;

      const d = res.data;

      setPreviewData(d);

      setFormData({
        name: d.name || "",
        surname: d.surname || "",
        dateOfBirth: d.dateOfBirth ? new Date(d.dateOfBirth) : null,
        placeOfBirth: d.placeOfBirth || "",
        citizenship: d.citizenship || "",
        otherCitizen: d?.otherCitizenship?.hasOther ? "Yes" : "No",
        otherCountry: d?.otherCitizenship?.country || "",
        motherTongue: d.motherTongue || "",
        religion: d.religion || "",
        gender: d.gender || "",
        height: d.heightInInch || "",
        casteImageUrl: d.caste || "",
        isSpouseApplication: d.isSpouseApplication || false,
      });

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  const handleChange = useCallback(({ target }) => {
    const { name, value, type, checked } = target;
    const finalValue = type === "checkbox" ? checked : value;

    setFormData(prev => ({ ...prev, [name]: finalValue }));
    setErrors(prev => ({ ...prev, [name]: "" }));

  }, []);


  const validateForm = () => {
    let e = {};

    if (!formData.name.trim()) e.name = "Required";
    if (!formData.surname.trim()) e.surname = "Required";
    if (!formData.dateOfBirth) e.dateOfBirth = "Required";
    if (!formData.placeOfBirth.trim()) e.placeOfBirth = "Required";
    if (!formData.citizenship.trim()) e.citizenship = "Required";
    if (!formData.gender) e.gender = "Required";
    if (!formData.height) e.height = "Required";

    return e;
  };


  const handleUpload = async (file) => {

    if (!file) return;

    if (file.size > 2 * 1024 * 1024)
      return showErrorToast("File must be under 2MB");

    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const fd = new FormData();
      fd.append("file", file);

      const res = await axios.post(
        `${BACKEND_URL_OUT}/uploadImage`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
        }
      );

      setFormData(prev => ({
        ...prev,
        casteImageUrl: res?.data?.data?.imageUrl || ""
      }));

      showSuccessToast("Image Uploaded!");

    } catch {
      showErrorToast("Upload failed");
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {

    e.preventDefault();

    const v = validateForm();
    setErrors(v);

    if (Object.keys(v).length) return;

    const payload = {
      name: formData.name,
      surname: formData.surname,
      dateOfBirth: formData.dateOfBirth.toISOString(),
      placeOfBirth: formData.placeOfBirth,
      citizenship: formData.citizenship,
      isSpouseApplication: formData.isSpouseApplication,

      otherCitizenship: {
        hasOther: formData.otherCitizen === "Yes",
        country:
          formData.otherCitizen === "Yes"
            ? formData.otherCountry
            : null,
      },

      motherTongue: formData.motherTongue || null,
      religion: formData.religion || null,
      gender: formData.gender || null,
      heightInInch: Number(formData.height) || null,
      caste: formData.casteImageUrl || null,
    };

    try {
      setLoading(true);

      await makeRequestAuth(
        "post",
        "/user/createOrUpdateBasicProfile",
        payload
      );

      showSuccessToast("Profile Updated 🚀");

      fetchProfile();
      fetchProfileUser();

    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (date) => {
    if (!date) return "N/A";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const generateHeights = () => {
    const heights = [];

    for (let inches = 48; inches <= 120; inches++) {
      const feet = Math.floor(inches / 12);
      const inch = inches % 12;
      const cm = Math.round(inches * 2.54);

      heights.push({
        value: inches,
        label: `${feet}' ${inch}" • ${cm} cm`
      });
    }

    return heights;
  };

  const HEIGHT_OPTIONS = generateHeights();



  // const inputClass = "w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#132b45]/20 focus:border-[#132b45] outline-none transition-all bg-gray-50/50 hover:bg-white";

  return (
    <div className="min-h-screen bg-gray-50/50 py-12 px-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 overflow-hidden border border-gray-100">

          {/* Header */}
          <div className="bg-[#132b45] p-8 text-white flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Personal Profile</h1>
              <p className="text-blue-100 text-sm mt-1">Manage your professional identity and details</p>
            </div>
            {previewData?.status === "reject" && (
              <button
                onClick={() => setPreviewData(null)}
                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
              >
                <Edit3 size={20} />
              </button>
            )}
          </div>

          <div className="p-8">
            {loading && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-50 flex items-center justify-center">
                <Loader2 className="animate-spin text-[#132b45]" size={40} />
              </div>
            )}

            {!previewData ? (
              <form onSubmit={handleSubmit} className="space-y-8">

                {/* Section 1: Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField label="First Name" icon={User} error={errors.name}>
                    <input name="name" value={formData.name} onChange={handleChange} className={inputClass} placeholder="John" />
                  </FormField>

                  <FormField label="Surname" icon={User} error={errors.surname}>
                    <input name="surname" value={formData.surname} onChange={handleChange} className={inputClass} placeholder="Doe" />
                  </FormField>

                  <FormField label="Date of Birth" icon={Calendar} error={errors.dateOfBirth}>
                    <input
                      type="date"
                      value={formData.dateOfBirth ? formData.dateOfBirth.toISOString().split('T')[0] : ''}
                      onChange={(e) => {
                        const dateVal = e.target.value ? new Date(e.target.value) : null;
                        setFormData((p) => ({ ...p, dateOfBirth: dateVal }));
                      }}
                      className={inputClass}
                      max={new Date().toISOString().split('T')[0]} // Limits selection to today or earlier
                    />
                  </FormField>

                  <FormField label="Gender" icon={User} error={errors.gender}>
                    <select name="gender" value={formData.gender} onChange={handleChange} className={inputClass}>
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer_not_to_say">Prefer Not to Say</option>
                    </select>
                  </FormField>
                </div>

                <hr className="border-gray-100" />

                {/* Section 2: Origin & Identity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField label="Place of Birth" icon={MapPin} error={errors.placeOfBirth}>
                    <input name="placeOfBirth" value={formData.placeOfBirth} onChange={handleChange} className={inputClass} placeholder="City, Country" />
                  </FormField>

                  <FormField label="Primary Citizenship" icon={Globe}>
                    <input name="citizenship" value={formData.citizenship} onChange={handleChange} className={inputClass} placeholder="e.g. Indian" />
                  </FormField>

                  <FormField label="Mother Tongue" icon={Languages}>
                    <input name="motherTongue" value={formData.motherTongue} onChange={handleChange} className={inputClass} />
                  </FormField>

                  <FormField label="Religion">
                    <select name="religion" value={formData.religion} onChange={handleChange} className={inputClass}>
                      <option value="">Select Religion</option>
                      <option>Hindu</option>
                      <option>Muslim</option>
                      <option>Christian</option>
                      <option>Sikh</option>
                      <option>Bodh</option>
                      <option>Jain</option>
                    </select>
                  </FormField>
                </div>

                <hr className="border-gray-100" />

                {/* Section 3: Additional Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField label="Dual Citizenship?">
                    <select name="otherCitizen" value={formData.otherCitizen} onChange={handleChange} className={inputClass}>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </FormField>

                  {formData.otherCitizen === "Yes" && (
                    <FormField label="Second Country" icon={Globe}>
                      <select name="otherCountry" value={formData.otherCountry} onChange={handleChange} className={inputClass}>
                        <option value="">Select Country</option>
                        {countries.map((c, i) => <option key={i} value={c}>{c}</option>)}
                      </select>
                    </FormField>
                  )}

                  <FormField label="Height" error={errors.height}>
                    <select
                      name="height"
                      value={formData.height}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="">Select Height</option>

                      {HEIGHT_OPTIONS.map(h => (
                        <option key={h.value} value={h.value}>
                          {h.label}
                        </option>
                      ))}
                    </select>
                  </FormField>


                  <FormField label="Applying for Spouse?">
                    <input
                      type="checkbox"
                      name="isSpouseApplication"
                      checked={formData.isSpouseApplication}
                      onChange={(e) =>
                        setFormData(prev => ({
                          ...prev,
                          isSpouseApplication: e.target.checked
                        }))
                      }
                    />
                  </FormField>

                  <FormField label="Caste Document" icon={ImageIcon}>
                    <div className="flex items-center gap-4">
                      <label className="flex-1 cursor-pointer bg-white border-2 border-dashed border-gray-200 hover:border-[#132b45] rounded-xl p-2 text-center transition-all group">
                        <input type="file" className="hidden" onChange={(e) => handleUpload(e.target.files[0])} />
                        <span className="text-xs text-gray-500 group-hover:text-[#132b45]">
                          {formData.casteImageUrl ? "Change Image" : "Click to Upload"}
                        </span>
                      </label>
                      {formData.casteImageUrl && (
                        <img src={formData.casteImageUrl} className="w-12 h-12 rounded-lg object-cover border" alt="preview" />
                      )}
                    </div>
                  </FormField>

                </div>



                <button className="group relative w-full bg-[#132b45] hover:bg-[#1a3a5c] text-white font-bold py-4 px-6 rounded-2xl transition-all shadow-lg hover:shadow-[#132b45]/20 flex items-center justify-center gap-2 overflow-hidden">
                  <span className="relative z-10 text-lg">Update Profile</span>
                  <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            ) : (
              /* Preview Mode */
              <div className="py-10">

                {/* Top Profile */}
                <div className="text-center">
                  {/* <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center border-4 border-white shadow-md">
                    <User size={40} className="text-gray-400" />
                  </div> */}

                  <h2 className="text-3xl font-extrabold text-[#132b45]">
                    {previewData.name} {previewData.surname}
                  </h2>

                  {previewData?.uci && (<h2 className="text-black mt-2 capitalize">
                    UCI: {previewData.uci}
                  </h2>)}

                  <p className="text-black mt-2 capitalize">
                    Gender: {previewData.gender} - Religion: {previewData.religion || "N/A"}
                  </p>
                </div>


                {/* Info Grid */}
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 max-w-3xl mx-auto">

                  <InfoCard label="Date of Birth" value={formatDate(previewData.dateOfBirth)} />

                  <InfoCard label="Place of Birth" value={previewData.placeOfBirth} />

                  <InfoCard label="Citizenship" value={previewData.citizenship} />

                  <InfoCard
                    label="Dual Citizenship"
                    value={
                      previewData?.otherCitizenship?.hasOther
                        ? previewData.otherCitizenship.country
                        : "No"
                    }
                  />

                  <InfoCard label="Mother Tongue" value={previewData.motherTongue || "N/A"} />

                  <InfoCard
                    label="Height"
                    value={`${Math.floor(previewData.heightInInch / 12)}' ${previewData.heightInInch % 12}"`}
                  />

                  <InfoCard
                    label="Spouse Application"
                    value={previewData.isSpouseApplication ? "Yes" : "No"}
                  />

                  <InfoCard label="Status" value={previewData.status} />

                </div>


                {/* Caste Document */}
                {previewData.caste && (
                  <div className="mt-10 text-center">
                    <p className="text-sm text-gray-400 mb-2 uppercase tracking-wider">
                      Caste / Profile Document
                    </p>

                    <img
                      src={previewData.caste}
                      alt="caste"
                      className="w-40 h-40 object-cover rounded-2xl mx-auto border shadow-sm"
                    />
                  </div>
                )}


                {/* Edit Button */}
                {previewData.status === "reject" && (<div className="text-center">
                  <button
                    onClick={() => setPreviewData(null)}
                    className="mt-10 inline-flex items-center gap-2 text-[#132b45] font-semibold border-b-2 border-transparent hover:border-[#132b45] transition-all"
                  >
                    <Edit3 size={18} />
                    Edit Personal Details
                  </button>
                </div>)}
              </div>

            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default ApplicantForm;